import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useTenant } from "@/lib/tenant";
import { PageHeader, Panel, Stat, SevBadge, Empty } from "@/components/Bits";
import OnboardingCard from "@/components/OnboardingCard";
import NewCustomerButton from "@/components/NewCustomerButton";
import { AdminTour, GettingStarted, HelpTip } from "@/components/AdminGuide";
import { useAuth } from "@/lib/auth";
import { Activity, Radar, ShieldCheck, ShieldAlert, Zap, CircleHelp } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const ScoreGauge = ({ score }) => {
  const color = score >= 85 ? "#10B981" : score >= 65 ? "#facc15" : score >= 40 ? "#FF9900" : "#FF3333";
  const R = 54; const C = 2 * Math.PI * R;
  const off = C - (score / 100) * C;
  return (
    <div className="relative w-40 h-40 flex items-center justify-center">
      <svg width="140" height="140" className="-rotate-90">
        <circle cx="70" cy="70" r={R} stroke="#1f1f22" strokeWidth="8" fill="none" />
        <circle cx="70" cy="70" r={R} stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={C} strokeDashoffset={off} strokeLinecap="butt"
          style={{ transition: "stroke-dashoffset 600ms" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display text-4xl font-extrabold tracking-tight" style={{ color }}>{score}</div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">/ 100</div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { tenantId, current } = useTenant();
  const { user } = useAuth();
  const isOperator = user?.role === "admin" || user?.role === "analyst";
  const [tourKey, setTourKey] = useState(0);
  const nav = useNavigate();
  const base = isOperator ? "/admin" : "/customer";
  const qc = useQueryClient();
  const [discovered, setDiscovered] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", tenantId],
    queryFn: () => api.get(`/dashboard/${tenantId}`).then((r) => r.data),
    enabled: !!tenantId,
    refetchInterval: 20000,
  });

  const auto = useMutation({
    mutationFn: () => api.post(`/autodiscover/${tenantId}`).then((r) => r.data),
    onSuccess: (d) => {
      setDiscovered(d.endpoint);
      toast.success(`AutoDiscover picked up ${d.endpoint.hostname}`, { description: `${d.endpoint.user} · ${d.endpoint.ip}` });
      qc.invalidateQueries({ queryKey: ["dashboard", tenantId] });
    },
  });

  if (!tenantId) return <Empty msg="Select an MSP tenant" />;
  if (isLoading || !data) return <Empty msg="Loading tenant data…" />;

  const risk = data.endpoint_risk;
  const stat = data.endpoint_status;

  return (
    <>
      <PageHeader title={current?.name || "Dashboard"} kicker={isOperator ? "Security Operations · Live" : "Customer Portal · Read-only · Live"}>
        {isOperator && (<>
        <HelpTip step="customer"><button data-testid="help-customer" className="text-zinc-500 hover:text-cyan-300 transition-colors duration-150" aria-label="How to onboard a customer"><CircleHelp size={15} /></button></HelpTip>
        <NewCustomerButton />
        <HelpTip step="agent"><button data-testid="help-agent" className="text-zinc-500 hover:text-cyan-300 transition-colors duration-150" aria-label="How AutoDiscover and agents work"><CircleHelp size={15} /></button></HelpTip>
        <button
          data-testid="autodiscover-btn"
          onClick={() => auto.mutate()}
          className="border border-cyan-400/60 bg-cyan-400/5 hover:bg-cyan-400/15 text-cyan-300 px-3 py-1.5 text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors duration-150"
        >
          <Radar size={13} /> {auto.isPending ? "SCANNING…" : "AutoDiscover"}
        </button>
        </>)}
      </PageHeader>

      {isOperator && data.setup && (
        <div className="px-6 pt-6">
          <GettingStarted stats={data.setup} tenant={current} onOpenTour={() => setTourKey((k) => k + 1)} />
          <AdminTour stats={data.setup} tenant={current} forceOpen={tourKey} />
        </div>
      )}

      <div className="p-6 grid grid-cols-12 gap-4">
        {!isOperator && <OnboardingCard tenantId={tenantId} />}
        {/* Score */}
        <Panel className="col-span-12 md:col-span-4" kicker="Security Score" title="Fleet Posture">
          <div className="p-6 flex items-center gap-6">
            <ScoreGauge score={data.security_score} />
            <div className="flex-1 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-zinc-400">Critical</span><span className="font-mono text-red-400">{risk.critical}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">High</span><span className="font-mono text-orange-400">{risk.high}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Medium</span><span className="font-mono text-yellow-400">{risk.medium}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Healthy</span><span className="font-mono text-emerald-400">{risk.low}</span></div>
            </div>
          </div>
        </Panel>

        {/* Stats */}
        <div className="col-span-12 md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Endpoints Online" value={stat.online} sub={`${stat.offline} offline`} tone="ok" />
          <Stat label="At Risk" value={stat.at_risk} sub="critical + high" tone="critical" />
          <Stat label="AI Incidents" value={data.incident_severity.critical + data.incident_severity.high + data.incident_severity.medium} sub={`${data.incident_severity.critical} critical`} tone="ai" />
          <Stat label="Phishing Blocked" value={data.email_risk.critical + data.email_risk.high} sub={`${data.email_risk.medium} suspicious`} tone="high" />
        </div>

        {/* AI incident feed */}
        <Panel className="col-span-12 xl:col-span-8" kicker="AI Correlation Engine" title="Active Incidents"
          right={<span className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">Claude Sonnet 5</span>}>
          <div className="divide-y hairline">
            {data.top_incidents.map((inc) => (
              <button key={inc.id} data-testid={`incident-row-${inc.id}`}
                onClick={() => nav(`${base}/incidents?open=${inc.id}`)}
                className="w-full text-left px-4 py-3 hover:bg-white/[0.03] flex items-start gap-4 transition-colors duration-150">
                <SevBadge sev={inc.severity} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{inc.title}</div>
                  <div className="text-xs text-zinc-500 truncate">{inc.summary}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {inc.mitre.map((m) => (
                      <span key={m} className="font-mono text-[10px] px-1.5 py-0.5 border hairline text-zinc-400">{m}</span>
                    ))}
                  </div>
                </div>
                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">{inc.status}</div>
              </button>
            ))}
            {data.top_incidents.length === 0 && <Empty msg="No active incidents" />}
          </div>
        </Panel>

        {/* Signals */}
        <Panel className="col-span-12 xl:col-span-4" kicker="Signal Feed" title="XDR Telemetry">
          <div className="p-4 space-y-3 text-sm">
            <SignalRow icon={ShieldCheck} label="Endpoint" val={stat.total} sub="devices onboarded" />
            <SignalRow icon={Activity} label="Events (24h)" val={data.recent_events_count} sub="raw signals correlated" />
            <SignalRow icon={ShieldAlert} label="Identity Alerts" val={data.identity_severity.critical + data.identity_severity.high} sub="AD / Entra ID / Google" />
            <SignalRow icon={Zap} label="Vulnerabilities" val={data.vuln_severity.critical + data.vuln_severity.high} sub="high / critical CVEs" />
          </div>
        </Panel>

        {discovered && (
          <Panel className="col-span-12 ai-pulse" kicker="AutoDiscover™ · New Endpoint" title={discovered.hostname}>
            <div className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
              <KV k="User" v={discovered.user} />
              <KV k="OS" v={discovered.os} />
              <KV k="IP" v={discovered.ip} mono />
              <KV k="Defender" v={discovered.defender ? "ON" : "OFF"} tone={discovered.defender ? "ok" : "critical"} />
              <KV k="Firewall" v={discovered.firewall ? "ON" : "OFF"} tone={discovered.firewall ? "ok" : "critical"} />
              <KV k="Risk" v={discovered.risk.toUpperCase()} tone={discovered.risk} />
            </div>
          </Panel>
        )}
      </div>
    </>
  );
}

const SignalRow = ({ icon: Icon, label, val, sub }) => (
  <div className="flex items-center gap-3 border hairline p-3">
    <Icon size={18} className="text-cyan-300" />
    <div className="flex-1 min-w-0">
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-[11px] text-zinc-500 truncate">{sub}</div>
    </div>
    <div className="font-display font-extrabold text-xl">{val}</div>
  </div>
);

const KV = ({ k, v, mono, tone }) => {
  const toneCls = {
    critical: "text-red-400", high: "text-orange-400",
    medium: "text-yellow-400", low: "text-emerald-400", ok: "text-emerald-400"
  }[tone] || "text-white";
  return (
    <div className="border hairline p-2">
      <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{k}</div>
      <div className={`text-sm font-semibold ${toneCls} ${mono ? "font-mono" : ""}`}>{v}</div>
    </div>
  );
};
